import {
  Button,
  Stack,
  Grid,
  Box,
  Title,
  Text,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import Line from "./Line";
import { MarkerSolidIcon } from ".././icons";
import StopSelector from "./StopSelector";
import { v4 as uuidv4 } from "uuid";

export default function ItineraryBuilder({
  tripType,
  updateLocation,
  locations,
  setLocations,
  formData,
  updateFormField,
  tripTypeTitle,
}) {
  const theme = useMantineTheme();
  const isRoundTrip = tripType === "round_trip";
  const addStopAtIndex = (index) => {
    const newStop = {
      id: uuidv4(),
      label: "",
      address: "",
      lat: null,
      lng: null,
      locationType: "stop",
    };

    setLocations((prev) => {
      const before = prev.slice(0, index);
      const after = prev.slice(index);
      return [...before, newStop, ...after];
    });
  };

  const removeCurrentStop = (index) => {
    setLocations((prev) => {
      const before = prev.slice(0, index);
      const after = prev.slice(index + 1);
      return [...before, ...after];
    });
  };

  const getPlaceholder = () => {
    switch (tripType) {
      case "shuttle":
        return "Please share any additional details about the shuttle requirements. This will help us create a plan that meets your needs";
      default:
        return "Add any additional detail you want us to know to create a travel plan for you";
    }
  };
  return (
    <Stack gap="lg">
      <Title>Itinerary · {tripTypeTitle}</Title>
      <Text size="sm">
        Please fill out as many details as you can. If you don’t have all
        details yet, you can let us know in the comments or add an itinerary in
        the next step.
      </Text>

      <Grid>
        {locations.map((location, index) => {
          const isLast =
            index === locations.length - 1 ||
            (isRoundTrip && index === locations.length - 2);

          return (
            <Grid.Col key={location.id} span={12}>
              <Grid align="stretch">
                <Grid.Col span={1}>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "108%",
                    }}
                  >
                    {!(isRoundTrip && index === locations.length - 1) && (
                      <MarkerSolidIcon
                        style={{ color: theme.colors.primary[6] }}
                        width={20}
                        height={20}
                      />
                    )}

                    {!isLast && <Line color={theme.colors.primary[6]} />}
                  </Box>
                </Grid.Col>

                <Grid.Col span={11}>
                  {index === 0 && (
                    <Box mb={10}>
                      <StopSelector
                        type="departure"
                        locations={locations}
                        location={location}
                        index={index}
                        updateLocation={updateLocation}
                        isRequired={!formData.specialRequests}
                        useDiy={formData.useDiy}
                      />
                      {tripType === "custom" && (
                        <Button
                          variant="outline"
                          mb={15}
                          mt={15}
                          onClick={() => addStopAtIndex(1)}
                        >
                          Add Stop
                        </Button>
                      )}
                    </Box>
                  )}

                  {index > 0 && index < locations.length - 1 && (
                    <Box mb={!isRoundTrip ? 10 : 0}>
                      <StopSelector
                        type="stop"
                        locations={locations}
                        location={location}
                        index={index}
                        updateLocation={updateLocation}
                        isRoundTrip={isRoundTrip}
                        isRequired={!formData.specialRequests}
                        removeCurrentStop={removeCurrentStop}
                        tripType={tripType}
                        useDiy={formData.useDiy}
                      />
                      {tripType === "custom" && (
                        <Stack align="flex-start">
                          {/* Removing (keep vehicle onsite checkbox) this until further discussions */}
                          {/* <Checkbox
                            size="sm"
                            mt={15}
                            value={formData.keepVehicleOnsite}
                            onChange={(event) => {
                              updateLocation(index, {
                                keepVehicleOnsite: event.currentTarget.checked,
                              });
                            }}
                            label={
                              <Group gap={6} align="center">
                                <Text span>Keep vehicle onsite</Text>

                                <Tooltip label="Vehicle will remain at this stop until the next trip segment">
                                  <InfoCircle
                                    style={{ color: "#646464" }}
                                    width={16}
                                    height={16}
                                  />
                                </Tooltip>
                              </Group>
                            }
                            styles={{
                              label: {
                                fontWeight: 400,
                                fontSize: "14px",
                                color: "#383838",
                                lineHeight: "125%",
                              },
                            }}
                          /> */}
                          <Button
                            mb={15}
                            mt={15}
                            variant="outline"
                            onClick={() => addStopAtIndex(index + 1)}
                          >
                            Add Stop
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  )}

                  {index === locations.length - 1 && !isRoundTrip && (
                    <StopSelector
                      type="destination"
                      locations={locations}
                      location={location}
                      index={index}
                      updateLocation={updateLocation}
                      isRoundTrip={isRoundTrip}
                      tripType={tripType}
                      isRequired={!formData.specialRequests}
                      useDiy={formData.useDiy}
                    />
                  )}
                </Grid.Col>
              </Grid>
            </Grid.Col>
          );
        })}
        <Grid.Col span={12}>
          <>
            <style>{`
            .special-request-placeholder textarea::placeholder {
              font-size: 14px !important;
              font-style: normal !important;
              font-weight: 400 !important;
            }
          `}</style>
            <Textarea
              className="special-request-placeholder"
              label={
                tripType === "shuttle" || tripType === "custom"
                  ? "Additional details"
                  : "Any special transportation needs?"
              }
              labelProps={{
                style: { fontWeight: "600", marginBottom: "12px" },
              }}
              styles={{
                input: {
                  backgroundColor: "#F6F1F1",
                  borderRadius: "6px",
                  border: "0.5px solid black",
                  height: "105px",
                  padding: "16px 20px",
                  color: "#000",
                },
              }}
              value={formData.specialRequests}
              onChange={(e) =>
                updateFormField("specialRequests", e.currentTarget.value)
              }
              placeholder={getPlaceholder()}
            />
          </>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
